import React, {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {selectInspectedHexKey, selectSelectedToken} from '../../../redux/reducers/privateStateReducer/selectors';
import './ConstructionPhaseRules.css';

import BuildingDescription from "../../BuildingDescription/BuildingDescription";
import {CENTRAL_QUARTER_PREFIX, SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {useIsCurrentUserTurn} from "../../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {selectHexes, selectPhase, selectSpecialBuildings} from "../../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../../constants/phases";

const ConstructionPhaseRules = () => {
  const phase = useSelector(selectPhase);
  const selectedToken = useSelector(selectSelectedToken);
  const inspectedHexKey = useSelector(selectInspectedHexKey);
  const hexesByKey = useSelector(selectHexes);
  const specialBuildingsByKey = useSelector(selectSpecialBuildings);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const contentRef = useRef(null);

  const getModalBuildingId = (token) => {
    if (!token) return null;

    if (token.startsWith(CENTRAL_QUARTER_PREFIX)) {
      return `${CENTRAL_QUARTER_PREFIX}neutral`;
    }

    return token;
  };

  // Цель автоскролла: при активной инспекции ячейки (ссылка «Подробнее» в
  // InspectedCellPanel, доступна в том числе в свою фазу строительства) —
  // строение инспектируемой ячейки (специальное приоритетнее базового
  // квартала под ним); без инспекции в свою фазу строительства (InfoButton) —
  // строение, выбранное в тулбаре
  let autoScrollToken = null;
  if (inspectedHexKey) {
    autoScrollToken = specialBuildingsByKey[inspectedHexKey] || hexesByKey[inspectedHexKey];
  } else if (phase === PHASES.CONSTRUCTION && isCurrentUserTurn) {
    autoScrollToken = selectedToken;
  }

  useEffect(() => {
    const modalBuildingId = getModalBuildingId(autoScrollToken);
    if (!modalBuildingId || !contentRef.current) return;

    const target = contentRef.current.querySelector(`[data-building-title-id="${modalBuildingId}"]`);
    if (!target) return;

    // Прокрутка/подсветка происходит внутри скроллящегося контейнера модалки,
    // это родитель корневого элемента этого компонента (см. RulesModal)
    const scrollContainer = contentRef.current.parentElement;
    if (!scrollContainer) return;

    let animationTimer;

    const runAnimation = () => {
      const row = target.closest('.construction-phase-info-table_row');
      const icon = row && row.querySelector('.construction-phase-info-table_icon img');
      if (!icon) return;

      animationTimer = setTimeout(() => {
        icon.classList.add('construction-phase-info_building-icon-pulse');

        const handleAnimationEnd = () => {
          icon.classList.remove('construction-phase-info_building-icon-pulse');
          icon.removeEventListener('animationend', handleAnimationEnd);
        };

        icon.addEventListener('animationend', handleAnimationEnd);
      }, 300);
    };

    const timer = setTimeout(() => {
      target.scrollIntoView({behavior: 'smooth', block: 'center'});
    }, 0);

    const onScrollEnd = () => {
      clearTimeout(scrollEndFallbackTimer);
      runAnimation();
    };

    scrollContainer.addEventListener('scrollend', onScrollEnd, {once: true});
    const scrollEndFallbackTimer = setTimeout(onScrollEnd, 600);

    return () => {
      clearTimeout(timer);
      clearTimeout(scrollEndFallbackTimer);
      clearTimeout(animationTimer);
      scrollContainer.removeEventListener('scrollend', onScrollEnd);
    };
  }, [autoScrollToken]);

  const action = <img
    className={'policy-description-icon policy-description-icon-margin-left'}
    src={'/img/icons/action.png'}
    alt={'Действие'}
    title={'Действие в Фазе Строительства'}
  />;
  const actionMarginRightLeft = <img
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={'/img/icons/action.png'}
    alt={'Действие'}
    title={'Действие в Фазе Строительства'}
  />;
  const coins = <img
    className={'policy-description-icon policy-description-icon-margin-left construction-phase-info_coins-icon'}
    src={'/img/icons/coins.png'}
    alt={'Монеты'}
    title={'Монеты'}
  />;
  const initiative = <img
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={'/img/icons/initiative.png'}
    alt={'Инициатива'}
    title={'Инициатива (право первого хода в фазе)'}
  />;
  const undo = <img
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={'/img/icons/return.png'}
    alt={'Отмена'}
    title={'Отменить действие'}
  />;

  const hammer = <img
    className={'policy-description-icon policy-description-icon-margin-right-left'}
    src={'/img/icons/hammer brown.png'}
    alt={'Строительство'}
    title={'Строительство'}
  />;
  const specialBuildings = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right-left-min'}
    src={'/img/hexes/special buildings/special building.c496c562.png'}
    alt={'Специальное строение'}
    title={'Специальное строение'}
  />;
  const quarterMarginLeft = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-left'}
    src={'/img/hexes/quarter.f69660fa.png'}
    alt={'Базовый квартал'}
    title={'Базовый квартал'}
  />;
  const centralQuarter = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right-left'}
    src={'/img/icons/central quarter.7dd51bfd.png'}
    alt={'Центральный квартал'}
    title={'Центральный квартал'}
  />;
  const quarterMarginRight = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-right'}
    src={'/img/hexes/quarter.f69660fa.png'}
    alt={'Базовый квартал'}
    title={'Базовый квартал'}
  />;
  const roadBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/road.1f281346.png'}
    alt={'Дорога'}
    title={'Дорога'}
  />;
  const barracksBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/barracks.efab12f7.png'}
    alt={'Казарма'}
    title={'Казарма'}
  />;
  const farmBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/farm.699e6dfc.png'}
    alt={'Ферма'}
    title={'Ферма'}
  />;
  const centralQuarterBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/icons/central quarter.7dd51bfd.png'}
    alt={'Центральный квартал'}
    title={'Центральный квартал'}
  />;
  const quarterBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/quarter.f69660fa.png'}
    alt={'Базовый квартал'}
    title={'Базовый квартал'}
  />;
  const palaceBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/palace.4b9c923b.png'}
    alt={'Дворец'}
    title={'Дворец'}
  />;
  const warehouseBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/warehouse.adf397b3.png'}
    alt={'Склад'}
    title={'Склад'}
  />;
  const militaryCampBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/military camp.1216e2cd.png'}
    alt={'Лагерь'}
    title={'Лагерь'}
  />;
  const arsenalBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/arsenal.7f5423b5.png'}
    alt={'Арсенал'}
    title={'Арсенал'}
  />;
  const fortressBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/fortress button.6fed26ae.png'}
    alt={'Крепость'}
    title={'Крепость'}
  />;
  const templeBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/temple.17c08b3d.png'}
    alt={'Храм'}
    title={'Храм'}
  />;
  const guildBuilding = <img
    className={'construction-phase-info_building-icon'}
    src={'/img/hexes/special buildings/guild.ee26d2b8.png'}
    alt={'Гильдия'}
    title={'Гильдия'}
  />;


  const fortressIcon = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-left'}
    src={'/img/hexes/special buildings/fortress button.6fed26ae.png'}
    alt={'Крепость'}
    title={'Крепость'}
  />;
  const palaceIcon = <img
    className={'policy-description-icon-building policy-description-icon-building-margin-left'}
    src={'/img/hexes/special buildings/palace.4b9c923b.png'}
    alt={'Дворец'}
    title={'Дворец'}
  />;

  return (
    <div className="construction-phase-info-modal_content" ref={contentRef}>
      <h2 className="construction-phase-info-modal_title text-construction">
        <img
          className={'rules-page_title-icon'}
          src={'/img/icons/hammer brown.png'}
          alt={''}
          aria-hidden={'true'}
        />
        Фаза Строительства
      </h2>

      <div className="construction-phase-info-modal_section">
        <div className={'policy-description_line'}>
          В этой фазе игроки могут:
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          • размещать строения на поле за {actionMarginRightLeft} и {coins}
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          • перехватывать инициативу {initiative} за {action}
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          • покупать политики за {coins}
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <div className={'policy-description_line'}>
          Перехватить инициативу {initiative} может только тот, кто ею не владеет:
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          игрок, который и так ходит первым, перехватывать не может.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          Перехват в раунде только один: после него остальные игроки
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          инициативу в этом раунде уже не перехватят.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          Перехвативший ходит первым с ближайшей фазы Войны этого же раунда.
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <div className={'policy-description_line'}>
          Базовый лимит действий {actionMarginRightLeft} = 2.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          Игрок может увеличить лимит {action}
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          строительством Дворца {palaceIcon}.
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <h2>Обычные строения</h2>

        <div className="construction-phase-info-table">
          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {roadBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription buildingId={TOKENS.ROAD} titleDataId={TOKENS.ROAD} renderPart="meta"/>
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={TOKENS.ROAD} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={TOKENS.ROAD} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {barracksBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription buildingId={TOKENS.BARRACKS} titleDataId={TOKENS.BARRACKS} renderPart="meta"/>
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={TOKENS.BARRACKS} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={TOKENS.BARRACKS} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {farmBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription buildingId={TOKENS.FARM} titleDataId={TOKENS.FARM} renderPart="meta"/>
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={TOKENS.FARM} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={TOKENS.FARM} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {centralQuarterBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={`${CENTRAL_QUARTER_PREFIX}neutral`} titleDataId={`${CENTRAL_QUARTER_PREFIX}neutral`}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={`${CENTRAL_QUARTER_PREFIX}neutral`} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={`${CENTRAL_QUARTER_PREFIX}neutral`} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {quarterBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription buildingId={TOKENS.QUARTER} titleDataId={TOKENS.QUARTER} renderPart="meta"/>
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={TOKENS.QUARTER} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={TOKENS.QUARTER} renderPart="condition"/>
            </div>
          </div>
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <h2>Специальные строения</h2>

        <div className={'policy-description_line policy-description_line-margin-top-paragraph-large'}>
          <span className={'text-controls policy-description_line'}>
            Условие {hammer} (общее для всех, кроме {fortressIcon}):
          </span>
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          {specialBuildings} строятся поверх свободного {quarterMarginLeft},
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          который не поврежден и не находится в ЗК отряда противника.
        </div>

        <div className="construction-phase-info-table">
          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {palaceBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.PALACE} titleDataId={SPECIAL_BUILDING_TOKENS.PALACE}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.PALACE} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.PALACE} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {warehouseBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.WAREHOUSE} titleDataId={SPECIAL_BUILDING_TOKENS.WAREHOUSE}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.WAREHOUSE} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.WAREHOUSE} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {militaryCampBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.MILITARY_CAMP}
                titleDataId={SPECIAL_BUILDING_TOKENS.MILITARY_CAMP}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.MILITARY_CAMP} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.MILITARY_CAMP} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {arsenalBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.ARSENAL} titleDataId={SPECIAL_BUILDING_TOKENS.ARSENAL}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.ARSENAL} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.ARSENAL} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {templeBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.TEMPLE} titleDataId={SPECIAL_BUILDING_TOKENS.TEMPLE}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.TEMPLE} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.TEMPLE} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {guildBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.GUILD} titleDataId={SPECIAL_BUILDING_TOKENS.GUILD}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.GUILD} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.GUILD} renderPart="condition"/>
            </div>
          </div>

          <div className="construction-phase-info-table_row">
            <div className="construction-phase-info-table_icon">
              {fortressBuilding}
            </div>
            <div className="construction-phase-info-table_details">
              <BuildingDescription
                buildingId={SPECIAL_BUILDING_TOKENS.FORTRESS} titleDataId={SPECIAL_BUILDING_TOKENS.FORTRESS}
                renderPart="meta"
              />
            </div>
            <div className="construction-phase-info-table_description">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.FORTRESS} renderPart="description"/>
            </div>
            <div className="construction-phase-info-table_condition">
              <BuildingDescription buildingId={SPECIAL_BUILDING_TOKENS.FORTRESS} renderPart="condition"/>
            </div>
          </div>
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <h2>Важные правила</h2>

        <h3 className={'construction-phase-info-modal_quarter-zone-title'}>Где можно строить</h3>
        <div className={'policy-description_line'}>
          Нельзя строить в зоне контроля (ЗК) отряда противника,
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          в том числе нейтрального: ни на ячейке с этим отрядом,
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          ни на смежных с ней ячейках.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Обычные строения ставятся на ячейку, смежную с путем строительства —
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          непрерывной цепочкой смежных непустых ячеек (строений, дорог, ключевых точек),
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          которая начинается от строения вашего города. Длина пути не ограничена.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Путь строительства не проходит через горы, моря,
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          ячейки чужих городов и ЗК отрядов противника.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          Строение вашего города в ЗК противника не служит началом пути.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          В первом раунде пути строительства нет: строить можно
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          только смежно своим кварталам, которые не в ЗК противника.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Специальные строения {specialBuildings} и крепость {fortressIcon} тоже нельзя
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          ставить на ячейку в ЗК отряда противника.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Поэтому отряд на ничейной ключевой точке держит в своей ЗК
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          все смежные с ней ячейки: пока он там стоит, другие игроки
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          не могут построить рядом квартал и забрать эту точку.
        </div>

        <h3 className={'construction-phase-info-modal_quarter-zone-title'}>Зона влияния кварталов</h3>
        <div className={'policy-description_line'}>
          Кварталы {centralQuarter} {quarterMarginRight} разных городов
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          не могут быть расположены
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          ближе 2 ячеек друг к другу.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Игра поможет вам учесть это ограничение через специальный оверлей,
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          который следующим образом подсвечивает ячейки,
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          на которые данное ограничение влияет:
        </div>

        <div className={'construction-phase-info-modal_quarter-zone-example'}>
          <div className={'construction-phase-info-modal_quarter-zone-example-icon'}>
            <img src={'/img/icons/cell.png'} alt={'Пустая ячейка'}/>
            <img src={'/img/icons/quarter_zone.png'} alt={'Зона влияния кварталов'}/>
          </div>
          <div className={'construction-phase-info-modal_quarter-zone-example-text'}>
            <div className={'policy-description_line'}>
              Такая штриховка на ячейке означает,
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              что здесь <span className={'policy-description-icon-margin-left text-war'}>нельзя построить</span>
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              ни Центральный квартал {centralQuarter},
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              ни Базовый квартал {quarterMarginLeft}.
            </div>
          </div>
        </div>

        <div className={'construction-phase-info-modal_quarter-zone-example'}>
          <div className={'construction-phase-info-modal_quarter-zone-example-icon'}>
            <img src={'/img/icons/cell.png'} alt={'Пустая ячейка'}/>
            <img src={'/img/icons/base_quarter_allowed.png'} alt={'Зона влияния кварталов'}/>
          </div>
          <div className={'construction-phase-info-modal_quarter-zone-example-text'}>
            <div className={'policy-description_line'}>
              Такая штриховка на ячейке означает,
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              что здесь <span className={'policy-description-icon-margin-left text-war'}>нельзя построить</span>
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              Центральный квартал {centralQuarter}.
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              Но
              <span
                className={'policy-description-icon-margin-right-left text-success'}
              >
                можно построить
              </span>
            </div>
            <div className={'policy-description_line policy-description_line-margin-top'}>
              Базовый квартал {quarterMarginLeft}.
            </div>
          </div>
        </div>

        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Штриховка может не учитывать отряды: в ЗК отряда противника
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          строить все равно нельзя.
        </div>
      </div>

      <div className="construction-phase-info-modal_section">
        <h3>Отмена действий</h3>
        <div className={'policy-description_line'}>
          Действия текущего хода можно
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          отменить кнопкой {undo} в нижней панели.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          После завершения хода игрок
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          никак не может отменить
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          выполненные действия.
        </div>
        <div className={'policy-description_line policy-description_line-margin-top-paragraph'}>
          Если время хода вышло, а новые строения
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          размещены некорректно, отменяются только они
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          и действия, которые без них нельзя было сделать
        </div>
        <div className={'policy-description_line policy-description_line-margin-top'}>
          (подробнее — «Отмена действий» в разделе «Интерфейс»).
        </div>
      </div>
    </div>
  );
};

export default ConstructionPhaseRules;
